import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight, X, Sparkles, BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';

interface Step {
  id: string;
  label: string;
  description: string;
  cta: string;
  path: string;
  check: () => Promise<boolean>;
}

interface OnboardingPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function OnboardingPanel({ isOpen, onClose }: OnboardingPanelProps) {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<{ step: Step; done: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [allDone, setAllDone] = useState(false);

  const standSteps: Step[] = [
    {
      id: 'profile',
      label: 'Compléter votre profil',
      description: 'Atteignez 80% pour être visible par les organisateurs',
      cta: 'Compléter',
      path: '/my-stand',
      check: async () => {
        if (!user) return false;
        const snap = await getDoc(doc(db, 'stands', user.uid));
        if (!snap.exists()) return false;
        const d = snap.data();
        const critical = [d.name, d.standType, d.description, d.region, d.logo, d.bannerUrl];
        const recommended = [d.brandStory, d.galleryUrls?.length > 0, d.instagram || d.tiktok, d.experienceRange, d.pastFestivals, d.insuranceUrl, d.kbisUrl, d.priceRange];
        const score = Math.round(((critical.filter(Boolean).length / critical.length) * 0.65 + (recommended.filter(Boolean).length / recommended.length) * 0.35) * 100);
        return score >= 80;
      },
    },
    {
      id: 'apply',
      label: 'Faire votre première candidature',
      description: 'Postulez à un festival qui vous correspond',
      cta: 'Explorer les festivals',
      path: '/home',
      check: async () => {
        if (!user) return false;
        const q = query(collection(db, 'applications'), where('applicantId', '==', user.uid), limit(1));
        const snap = await getDocs(q);
        return !snap.empty;
      },
    },
    {
      id: 'notify',
      label: 'Activer les notifications',
      description: 'Ne manquez aucune réponse d\'un organisateur',
      cta: 'Activer',
      path: '/settings',
      check: async () => {
        if (!user) return false;
        const snap = await getDoc(doc(db, 'users', user.uid));
        return snap.exists() && snap.data()?.notificationsEnabled === true;
      },
    },
  ];

  const festivalSteps: Step[] = [
    {
      id: 'create',
      label: 'Créer votre premier festival',
      description: 'Renseignez les infos de votre événement en quelques minutes',
      cta: 'Créer un festival',
      path: '/choose-creation-method',
      check: async () => {
        if (!user) return false;
        const q = query(collection(db, 'festivals'), where('ownerId', '==', user.uid), limit(1));
        const snap = await getDocs(q);
        return !snap.empty;
      },
    },
    {
      id: 'publish',
      label: 'Publier votre festival',
      description: 'Rendez-le visible pour recevoir des candidatures',
      cta: 'Mes festivals',
      path: '/my-festivals',
      check: async () => {
        if (!user) return false;
        const q = query(collection(db, 'festivals'), where('ownerId', '==', user.uid), where('status', '==', 'published'), limit(1));
        const snap = await getDocs(q);
        return !snap.empty;
      },
    },
    {
      id: 'application',
      label: 'Recevoir une première candidature',
      description: 'Les exposants vont vous trouver et postuler',
      cta: 'Voir mes candidatures',
      path: '/my-festivals',
      check: async () => {
        if (!user) return false;
        const q = query(collection(db, 'applications'), where('festivalOwnerId', '==', user.uid), limit(1));
        const snap = await getDocs(q);
        return !snap.empty;
      },
    },
  ];

  const promoterSteps: Step[] = [
    {
      id: 'apply',
      label: 'Déposer votre candidature promoteur',
      description: 'Rejoignez le programme d\'affiliation en 1 minute',
      cta: 'Devenir promoteur',
      path: '/become-promoter',
      check: async () => {
        if (!user) return false;
        const snap = await getDoc(doc(db, 'promoters', user.uid));
        return snap.exists();
      },
    },
    {
      id: 'sign',
      label: 'Signer votre contrat',
      description: 'Activez votre code affilié après validation',
      cta: 'Signer mon contrat',
      path: '/promoter/sign',
      check: async () => {
        if (!user) return false;
        const snap = await getDoc(doc(db, 'promoters', user.uid));
        return snap.exists() && snap.data()?.codeActive === true;
      },
    },
    {
      id: 'submit',
      label: 'Soumettre votre premier festival',
      description: 'Apportez un organisateur et touchez 20% de commission',
      cta: 'Soumettre un festival',
      path: '/promoter/submit-festival',
      check: async () => {
        if (!user) return false;
        const q = query(collection(db, 'promoterLeads'), where('promoterUid', '==', user.uid), limit(1));
        const snap = await getDocs(q);
        return !snap.empty;
      },
    },
  ];

  useEffect(() => {
    if (!user || !userRole) return;

    const key = `onboarding_dismissed_${user.uid}`;
    if (localStorage.getItem(key) === 'done') {
      setAllDone(true);
      setLoading(false);
      return;
    }

    const stepsToCheck = userRole === 'stand' ? standSteps
      : userRole === 'promoter' ? promoterSteps
      : festivalSteps;

    Promise.all(stepsToCheck.map(async s => ({ step: s, done: await s.check() }))).then(results => {
      setSteps(results);
      const finished = results.every(r => r.done);
      setAllDone(finished);
      if (finished) localStorage.setItem(key, 'done');
      setLoading(false);
    });
  }, [user, userRole]);

  const completedCount = steps.filter(s => s.done).length;
  const currentStep = steps.find(s => !s.done);
  const progress = steps.length > 0 ? (completedCount / steps.length) * 100 : 0;

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 z-50 bg-[#111218] border-l border-white/8 flex flex-col transition-transform duration-300 ease-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#FF5C00]" />
            <span className="font-bold text-white text-sm">Guide de démarrage</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
              ))}
            </div>
          ) : !user ? (
            <p className="text-sm text-gray-500 text-center mt-8">Connectez-vous pour voir votre progression</p>
          ) : allDone ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <CheckCircle className="w-10 h-10 text-emerald-400 mb-3" />
              <p className="font-bold text-white">Tout est prêt !</p>
              <p className="text-sm text-gray-500 mt-1">Vous avez complété toutes les étapes.</p>
            </div>
          ) : (
            <>
              {/* Progress */}
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#FF5C00]" />
                    <span className="text-xs font-bold text-white">
                      {completedCount}/{steps.length} étapes
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">{Math.round(progress)}%</span>
                </div>
                <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#FF5C00] rounded-full transition-all duration-700"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Steps */}
              <div className="space-y-2.5">
                {steps.map(({ step, done }, idx) => {
                  const isCurrent = step.id === currentStep?.step.id;
                  return (
                    <div
                      key={step.id}
                      className={`p-3.5 rounded-xl border transition-all ${
                        done
                          ? 'bg-emerald-500/5 border-emerald-500/15'
                          : isCurrent
                          ? 'bg-[#FF5C00]/8 border-[#FF5C00]/25'
                          : 'bg-white/3 border-white/5 opacity-45'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 mt-0.5">
                          {done ? (
                            <CheckCircle className="w-4.5 h-4.5 text-emerald-400" />
                          ) : (
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-black ${isCurrent ? 'border-[#FF5C00] text-[#FF5C00]' : 'border-white/20 text-gray-600'}`}>
                              {idx + 1}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold leading-tight ${done ? 'text-emerald-400 line-through' : isCurrent ? 'text-white' : 'text-gray-500'}`}>
                            {step.label}
                          </p>
                          {!done && (
                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{step.description}</p>
                          )}
                          {isCurrent && (
                            <button
                              onClick={() => handleNavigate(step.path)}
                              className="mt-2.5 flex items-center gap-1 text-xs font-black text-[#FF5C00] bg-[#FF5C00]/10 hover:bg-[#FF5C00]/20 px-3 py-1.5 rounded-lg transition-colors"
                            >
                              {step.cta}
                              <ChevronRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-white/8">
          <button
            onClick={() => { navigate('/support'); onClose(); }}
            className="w-full text-xs text-gray-500 hover:text-gray-300 transition-colors text-center"
          >
            Accéder au centre d'aide →
          </button>
        </div>
      </div>
    </>
  );
}
